// ============================================
// Demo API - Mock Data Only (No Backend)
// ============================================

import {
  mockFactories,
  mockEquipments,
  mockFactoryLines,
  mockCompanyDashboard,
  mockCompanyFactoryOverview,
  mockFactoryDashboard,
  mockMachineDashboard,
  mockMachineOverview,
  mockMachineCycle,
  mockMachineTemperature,
  mockMachineDrive,
  mockMachineAlarm,
  mockMachineSpc,
  mockUsers,
  mockRoleGroups,
  mockPermissions,
  mockNotices,
  mockRecentAlarms,
  mockWidgetConfig,
  mockWidgetData,
  mockCustomWidgetData,
  mockShotHistory,
  mockWatch1Data,
  mockWatch2Data,
  mockETLStatus,
  mockETLHistory,
  mockLoginLogs,
  mockAIResponse,
  mockFactoryDetail,
  mockEquipmentDetail,
  mockUserDetail,
  mockDBConnections,
  mockLSTMAnalysis,
  mockDailyReport,
  mockWeeklyReport,
  mockMonthlyReport
} from './mockData';

// ============================================
// Utility Functions
// ============================================

// 비동기 딜레이 (실제 API 호출 시뮬레이션)
const delay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

// 사용자 정보 캐시
let cachedUser = null;
let cachedSchemaName = null;

export const getSchemaName = () => {
  const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
  if (cachedUser === userStr && cachedSchemaName) {
    return cachedSchemaName;
  }
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      cachedUser = userStr;
      cachedSchemaName = user.schemaName || 'seoro';
      return cachedSchemaName;
    } catch (e) {
      return 'seoro';
    }
  }
  return 'seoro';
};

export const clearApiCache = () => {
  cachedUser = null;
  cachedSchemaName = null;
};

export const getHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// EquipmentBoard용 실 백엔드 URL (라즈베리파이 이벤트 수신)
// 다른 페이지는 mock으로 동작, 이 페이지만 실 API.
const EXPO_API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8086/api';

export const getApiBaseUrl = () => EXPO_API_BASE;
export const getBackendBaseUrl = () => EXPO_API_BASE.replace(/\/api$/, '');
export const getAiApiBaseUrl = () => 'mock://demo';

// 실 백엔드 호출 헬퍼 (EquipmentBoard 전용)
const expoRealFetch = async (path, label = 'expo api') => {
  try {
    const res = await fetch(`${EXPO_API_BASE}${path}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return json.success && json.data !== undefined ? json.data : json;
  } catch (err) {
    console.error(`Failed to ${label}:`, err);
    throw err;
  }
};

export const getFullImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }
  return '/images/placeholder.png';
};

export const refreshCsrfToken = () => null;

// ============================================
// Dashboard API
// ============================================

export const fetchEquipments = async () => {
  await delay();
  return mockEquipments;
};

// === EquipmentBoard 전용 실 백엔드 호출 (Pi 이벤트 반영) ===
export const fetchExpoEquipments = () =>
  expoRealFetch('/dashboard/equipments', 'fetch expo equipments');

export const fetchExpoMachineAlarm = (equipmentId) =>
  expoRealFetch(`/dashboard/machine/${equipmentId}/alarm`, `fetch expo machine alarm (eq=${equipmentId})`);

export const fetchExpoShotRawData = (equipmentId, limit = 100) =>
  expoRealFetch(`/machine/raw-data/${equipmentId}?limit=${limit}`, `fetch expo shot raw data (eq=${equipmentId})`);

// Pi 원본 이벤트 이력 (히스토리 모달용)
export const fetchExpoPiHistory = (machineId, limit = 100) =>
  expoRealFetch(`/pi/history/${machineId}?limit=${limit}`, `fetch expo pi history (eq=${machineId})`);

// Pi 원본 이벤트 이력 초기화 (해당 설비)
export const clearExpoPiHistory = async (machineId) => {
  try {
    const res = await fetch(`${EXPO_API_BASE}/pi/history/${machineId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return json?.data ?? json;
  } catch (err) {
    console.error(`clear expo pi history (eq=${machineId}) 실패:`, err);
    throw err;
  }
};

// 설비현황판 통합 스냅샷 — 모든 설비의 알람+샷을 한 번에
export const fetchExpoBoardSnapshot = (shotLimit = 50) =>
  expoRealFetch(`/dashboard/board-snapshot?shotLimit=${shotLimit}`, 'fetch expo board snapshot');

// === Machine 페이지용 — 백엔드가 완성된 알람 응답을 줌 (그대로 전달) ===
// 백엔드 응답: { summary, alarmHistory, paretoAnalysis, timeline, lastSyncTime }
export const fetchExpoMachineAlarmFormatted = (equipmentId) =>
  expoRealFetch(
    `/dashboard/machine/${equipmentId}/alarm`,
    `fetch expo machine alarm (eq=${equipmentId})`
  );

// === Machine 페이지용 — 실 백엔드 샷 데이터 그대로 전달 ===
// 백엔드(ShotRowDto)가 모든 필드를 완성된 형태로 채워줌:
//   shotTime / shotCount / cycleTime / injTime / ... : 실 데이터
//   chargePosi1 / temp1Current / injSpeed1 / qualityStatus / ... : 백엔드 더미 (시드 기반)
// 프론트는 응답 shape 만 {success, data:{shots}} 로 맞춰서 패스스루.
export const fetchExpoShotHistoryFormatted = async (equipmentId, limit = 100) => {
  const data = await expoRealFetch(
    `/machine/raw-data/${equipmentId}?limit=${limit}`,
    `fetch expo shot history (eq=${equipmentId})`
  );
  const shots = Array.isArray(data?.rows) ? data.rows : [];
  return { success: true, data: { shots } };
};

export const fetchMachineOverview = async (equipmentId) => {
  await delay();
  const equipment = mockEquipments.find(e => e.id === parseInt(equipmentId));
  return {
    ...mockMachineOverview,
    equipmentId: parseInt(equipmentId),
    equipmentName: equipment?.name || `INJ-${equipmentId}`,
    status: equipment?.status || 'RUNNING'
  };
};

export const fetchMachineCycle = async (equipmentId) => {
  await delay();
  return mockMachineCycle;
};

export const fetchMachineTemperature = async (equipmentId) => {
  await delay();
  return mockMachineTemperature;
};

export const fetchMachineDrive = async (equipmentId) => {
  await delay();
  return mockMachineDrive;
};

export const fetchMachineAlarm = async (equipmentId) => {
  await delay();
  return mockMachineAlarm;
};

export const fetchMachineSpc = async (equipmentId, parameter = 'cycle_time') => {
  await delay();
  return { ...mockMachineSpc, parameter };
};

// 양/불 탭 - 샷 히스토리
export const fetchShotHistory = async (equipmentId, limit = 100) => {
  await delay();
  return { success: true, data: mockShotHistory };
};

// 감시1 탭
export const fetchWatch1Data = async (equipmentId) => {
  await delay();
  return { success: true, data: mockWatch1Data };
};

// 감시2 탭
export const fetchWatch2Data = async (equipmentId) => {
  await delay();
  return { success: true, data: mockWatch2Data };
};

// LSTM 불량 원인 분석 API
export const fetchLSTMAnalysis = async (equipmentId, craftId, shotNumber) => {
  await delay();
  return { success: true, data: mockLSTMAnalysis };
};

export const fetchFactories = async () => {
  await delay();
  return mockFactories;
};

export const fetchCompanyDashboard = async () => {
  await delay();
  return { ...mockCompanyDashboard, lastSyncTime: new Date().toISOString() };
};

export const fetchCompanyFactoryOverview = async () => {
  await delay();
  return mockCompanyFactoryOverview;
};

export const fetchFactoryDashboard = async (factoryId) => {
  await delay();
  return mockFactoryDashboard[factoryId] || mockFactoryDashboard[1];
};

export const fetchMachineDashboard = async (factoryId, lineId = null) => {
  await delay();
  const dashboardData = mockMachineDashboard[factoryId] || mockMachineDashboard[1];
  let machines = dashboardData.machines || [];
  if (lineId) {
    machines = machines.filter(m => m.lineId === parseInt(lineId));
  }
  return {
    machines,
    lastSyncTime: dashboardData.lastSyncTime
  };
};

export const fetchFactoryLines = async (factoryId) => {
  await delay();
  return mockFactoryLines[factoryId] || [];
};

export const fetchLineEquipments = async (factoryId, lineId) => {
  await delay();
  return mockEquipments.filter(e => e.factoryId === parseInt(factoryId) && e.lineId === parseInt(lineId));
};

// ============================================
// Widget API
// ============================================

export const fetchWidgetConfig = async (userId) => {
  await delay();
  return mockWidgetConfig;
};

export const saveWidgetConfig = async (userId, widgets) => {
  await delay();
  return { success: true };
};

export const addWidget = async (userId, widget) => {
  await delay();
  return { success: true };
};

export const fetchMachineWidget = async (equipmentId, widgetType) => {
  await delay();
  const endpointMap = {
    '온도': 'temperature',
    '압력': 'pressure',
    '에너지': 'energy',
    '기본정보': 'basic-info',
    '기본사출정보': 'injection-info',
    '알람이력': 'alarm-history'
  };
  const endpoint = endpointMap[widgetType] || widgetType;
  const rawData = mockWidgetData[endpoint];

  // 위젯 타입별 데이터 형식 맞추기
  if (widgetType === '기본정보') {
    return { basicInfo: rawData?.basicInfo || rawData };
  } else if (widgetType === '알람이력') {
    return rawData; // 배열 그대로 반환
  } else if (widgetType === '기본사출정보') {
    return rawData; // 객체 그대로 반환
  } else {
    // 온도, 압력, 에너지 등 차트용 배열 데이터
    return { data: rawData || [] };
  }
};

export const fetchCustomWidget = async (equipmentId, widgetId) => {
  await delay();
  // 사용자모듈 위젯용 샷 데이터 반환
  return { data: mockCustomWidgetData };
};

// ============================================
// Factory Management API
// ============================================

export const fetchFactoriesForManagement = async (page = 0, size = 1000) => {
  await delay();
  return mockFactories.map(f => {
    const detail = mockFactoryDetail[f.id] || {};
    return {
      id: f.id,
      factoryName: f.factoryName,
      factoryCode: detail.factoryCode || `FAC-00${f.id}`,
      address: f.address,
      temperature: detail.temperature || 22.5,
      humidity: detail.humidity || 45.0,
      totalEquipment: f.totalEquipment,
      runningEquipment: f.runningEquipment,
      idleEquipment: f.idleEquipment,
      stoppedEquipment: f.stoppedEquipment,
      operationRate: detail.operationRate || Math.round((f.runningEquipment / f.totalEquipment) * 100),
      status: detail.status || 'NORMAL',
      lastInspectionDate: detail.lastInspectionDate || '2025-10-15',
      managerName: detail.managerName || '담당자'
    };
  });
};

export const fetchFactoryDetailForManagement = async (factoryId) => {
  await delay();
  return mockFactoryDetail[factoryId] || mockFactoryDetail[1];
};

export const createFactory = async (factoryData) => {
  await delay();
  return { id: Date.now(), ...factoryData };
};

export const updateFactory = async (factoryId, factoryData) => {
  await delay();
  return { id: factoryId, ...factoryData };
};

export const deleteFactory = async (factoryId) => {
  await delay();
  return { success: true };
};

// ============================================
// Equipment Management API
// ============================================

export const fetchEquipmentsForManagement = async (page = 0, size = 1000) => {
  await delay();
  return mockEquipments;
};

export const fetchEquipmentDetailForManagement = async (equipmentId) => {
  await delay();
  return mockEquipmentDetail[equipmentId] || mockEquipmentDetail[1];
};

export const createEquipment = async (equipmentData) => {
  await delay();
  return { id: Date.now(), ...equipmentData };
};

export const updateEquipment = async (equipmentId, equipmentData) => {
  await delay();
  return { id: equipmentId, ...equipmentData };
};

export const deleteEquipment = async (equipmentId) => {
  await delay();
  return { success: true };
};

// ============================================
// User Management API
// ============================================

export const fetchUsersForManagement = async (page = 0, size = 1000) => {
  await delay();
  return mockUsers;
};

export const fetchUserDetailForManagement = async (userId) => {
  await delay();
  return mockUserDetail[userId] || mockUserDetail[1];
};

export const fetchRoleGroups = async (page = 0, size = 1000) => {
  await delay();
  return mockRoleGroups;
};

export const createUser = async (userData) => {
  await delay();
  return { id: Date.now(), ...userData };
};

export const updateUser = async (userId, userData) => {
  await delay();
  return { id: userId, ...userData };
};

export const deleteUser = async (userId) => {
  await delay();
  return { success: true };
};

// ============================================
// 권한 관리 API
// ============================================

export const fetchPermissions = async () => {
  await delay();
  return mockPermissions;
};

export const createRoleGroup = async (roleGroupData) => {
  await delay();
  return { id: Date.now(), ...roleGroupData };
};

export const updateRoleGroup = async (roleGroupId, roleGroupData) => {
  await delay();
  return { id: roleGroupId, ...roleGroupData };
};

export const deleteRoleGroup = async (roleGroupId) => {
  await delay();
  return { success: true };
};

// ============================================
// AI API
// ============================================

// 객체 배열을 TableRenderer 형식으로 변환
const convertToTableFormat = (dataArray) => {
  if (!dataArray || dataArray.length === 0) {
    return { columns: [], rows: [] };
  }
  const columns = Object.keys(dataArray[0]);
  const rows = dataArray.map(item => columns.map(col => item[col]));
  return { columns, rows };
};

// 추천 질문 응답 생성
const getRecommendedQuestionResponse = (message) => {
  // 질문 1: 현재 가동 중인 설비 현황은?
  if (message.includes('가동') && message.includes('설비') && message.includes('현황')) {
    const total = mockEquipments.length;
    const running = mockEquipments.filter(e => e.status === 'RUNNING');
    const idle = mockEquipments.filter(e => e.status === 'IDLE');
    const stopped = mockEquipments.filter(e => e.status === 'STOPPED');
    const utilization = Math.round((running.length / total) * 100);

    // 설비별 가동률 (가동중이면 85~95%, 그 외 0)
    const utilizationByEquipment = mockEquipments.map(e => ({
      ...e,
      utilizationPct: e.status === 'RUNNING' ? 85 + Math.floor(Math.random() * 10) : 0,
      cycleTimeNow: e.status === 'RUNNING' ? +(e.targetCycleTime + (Math.random() * 2 - 1)).toFixed(1) : null,
    }));
    const avgUtilizationRunning = running.length > 0
      ? Math.round(utilizationByEquipment.filter(e => e.status === 'RUNNING')
          .reduce((sum, e) => sum + e.utilizationPct, 0) / running.length)
      : 0;

    // 공장별 가동 현황 + 가동률
    const factoryMap = new Map();
    utilizationByEquipment.forEach(e => {
      const key = e.factoryName || '본공장';
      if (!factoryMap.has(key)) factoryMap.set(key, { 공장: key, 가동중: 0, 대기중: 0, 정지: 0 });
      const row = factoryMap.get(key);
      if (e.status === 'RUNNING') row['가동중']++;
      else if (e.status === 'IDLE') row['대기중']++;
      else row['정지']++;
    });
    const factoryUtilization = Array.from(factoryMap.values()).map(f => {
      const t = f['가동중'] + f['대기중'] + f['정지'];
      return { ...f, total: t, rate: Math.round((f['가동중'] / t) * 100) };
    });

    // 라인별 가동 분석 (정지율 등)
    const lineMap = new Map();
    mockEquipments.forEach(e => {
      const key = `${e.factoryName}|${e.lineName}`;
      if (!lineMap.has(key)) lineMap.set(key, { factory: e.factoryName, line: e.lineName, total: 0, running: 0, idle: 0, stopped: 0 });
      const r = lineMap.get(key);
      r.total++;
      if (e.status === 'RUNNING') r.running++;
      else if (e.status === 'IDLE') r.idle++;
      else r.stopped++;
    });
    const worstLine = Array.from(lineMap.values())
      .filter(l => l.total > 0)
      .sort((a, b) => (b.stopped / b.total) - (a.stopped / a.total))[0];

    // 정지 설비 점검일 분석 (7일 이상 경과)
    const today = new Date();
    const stoppedOldInspect = stopped.filter(e => {
      if (!e.lastInspectionDate) return false;
      const days = (today - new Date(e.lastInspectionDate)) / (1000 * 60 * 60 * 24);
      return days > 7;
    });

    // 즉시 끌어올릴 수 있는 잠재 가동률 (대기 가동 시)
    const potentialRate = Math.round(((running.length + idle.length) / total) * 100);

    // 상세 테이블
    const tableData = utilizationByEquipment.map(e => ({
      설비명: e.equipmentName,
      공장: e.factoryName,
      라인: e.lineName,
      모델: e.model,
      톤수: `${e.tonnage}T`,
      상태: e.status === 'RUNNING' ? '가동중' : e.status === 'IDLE' ? '대기중' : '정지',
      가동률: e.status === 'RUNNING' ? `${e.utilizationPct}%` : '-',
      사이클: e.cycleTimeNow ? `${e.cycleTimeNow}초` : '-',
    }));

    // 인사이트 — 패턴 위주 (단순 수치 반복 X)
    const insights = [];

    // 공장 간 비교 (격차 10%p 이상일 때만)
    if (factoryUtilization.length >= 2) {
      const sorted = [...factoryUtilization].sort((a, b) => b.rate - a.rate);
      const best = sorted[0];
      const worst = sorted[sorted.length - 1];
      const gap = best.rate - worst.rate;
      if (gap >= 10) {
        insights.push({
          text: `${best['공장']} ${best.rate}% vs ${worst['공장']} ${worst.rate}% — ${worst['공장']} 가동 격차 ${gap}%p, 라인 운영 점검 필요.`,
          severity: 'warning',
        });
      }
    }

    // 가장 위험한 라인 (정지 집중)
    if (worstLine && worstLine.stopped > 0) {
      const stopRate = Math.round((worstLine.stopped / worstLine.total) * 100);
      insights.push({
        text: `${worstLine.factory} ${worstLine.line} 정지율 ${stopRate}% (${worstLine.total}대 중 ${worstLine.stopped}대) — 라인 단위 정밀 점검 필요.`,
        severity: 'critical',
      });
    }

    // 정지 설비 점검 이력 경과
    if (stoppedOldInspect.length > 0) {
      insights.push({
        text: `정지 ${stoppedOldInspect.length}대의 마지막 점검일이 7일 이상 경과 (${stoppedOldInspect.map(e => e.equipmentName).join(', ')}).`,
        severity: 'warning',
      });
    }

    // 가동 중인 설비 안정성
    if (running.length > 0) {
      insights.push({
        text: `가동 중 ${running.length}대의 평균 가동률 ${avgUtilizationRunning}% — 가동하는 설비는 안정적으로 작동 중입니다.`,
        severity: 'info',
      });
    }

    // 대기 가동 시 잠재력
    if (idle.length > 0 && potentialRate > utilization) {
      insights.push({
        text: `대기 ${idle.length}대 작업 지시 시 가동률 ${utilization}% → ${potentialRate}% 즉시 향상 가능.`,
        severity: 'info',
      });
    }

    // 권장 조치 — 우선순위별
    const recommendations = [];
    if (stopped.length > 0) {
      recommendations.push({
        priority: 'immediate',
        text: `정지 ${stopped.length}대 (${stopped.map(e => e.equipmentName).join(', ')}) — 점검 사유 확인 + 정비팀 배정.`,
      });
    }
    if (idle.length > 0) {
      recommendations.push({
        priority: 'today',
        text: `대기 ${idle.length}대 (${idle.map(e => e.equipmentName).join(', ')}) — 생산 계획 재확인 후 작업 지시.`,
      });
    }
    if (worstLine && worstLine.stopped > 0) {
      recommendations.push({
        priority: 'week',
        text: `${worstLine.factory} ${worstLine.line} 정지 패턴 7일 이력 분석 — 반복 발생 여부 확인.`,
      });
    }
    recommendations.push({
      priority: 'ongoing',
      text: `가동 중 ${running.length}대의 시간대별 가동률 추적 — 평균 ${avgUtilizationRunning}% 유지 여부 모니터링.`,
    });

    // 자연어 답변 — 분석가 톤. 한 줄 요약 + 맥락 + 핵심 관찰 + 우선 과제
    let context;
    if (utilization >= 80) context = '양호한 수준';
    else if (utilization >= 60) context = '보통 수준';
    else context = '낮은 수준';

    const summaryLine =
      `총 **${total}대** 중 **${running.length}대가 정상 가동** 중(**${utilization}%**) — ${context}입니다.`;

    const liftLine = (idle.length > 0 || stopped.length > 0)
      ? `정지 ${stopped.length}대 점검과 대기 ${idle.length}대 작업 지시로 **가동률 ${potentialRate}%까지 즉시 향상 가능**합니다.`
      : `현재 운영 효율은 안정 구간에 있습니다.`;

    const lineRiskLine = (worstLine && worstLine.stopped > 0)
      ? `**주의**: ${worstLine.factory} ${worstLine.line}의 정지율이 **${Math.round((worstLine.stopped / worstLine.total) * 100)}%** 로 다른 라인 대비 유의미하게 높습니다. 라인 단위 정밀 점검이 시급합니다.`
      : '';

    const stabilityLine = running.length > 0
      ? `가동 중인 ${running.length}대의 평균 가동률은 **${avgUtilizationRunning}%** 로 안정적이며, 추가 개선보다 **정지·대기 설비 활성화에 집중**하는 것이 효과적입니다.`
      : '';

    const naturalLanguage = [
      `### 가동 현황 분석`,
      summaryLine,
      liftLine,
      lineRiskLine,
      stabilityLine,
    ].filter(Boolean).join('\n\n');

    return {
      intent: 'query',
      natural_language: naturalLanguage,
      blocks: [
        // ① KPI 4개
        {
          type: 'stat-cards',
          content: {
            cards: [
              { label: '가동 중',     value: running.length, unit: '대', accent: '#475569' },
              { label: '대기 중',     value: idle.length,    unit: '대', accent: '#475569' },
              { label: '정지',        value: stopped.length, unit: '대', accent: stopped.length > 0 ? '#dc2626' : '#475569' },
              { label: '전체 가동률', value: utilization,    unit: '%',  accent: '#60a5fa' },
            ],
          },
        },
        // ② 상태 분포 도넛
        {
          type: 'chart',
          content: {
            type: 'pie',
            title: '설비 상태 분포',
            data: [
              { name: '가동중', value: running.length, fill: '#60a5fa' },
              { name: '대기중', value: idle.length,    fill: '#64748b' },
              { name: '정지',   value: stopped.length, fill: '#334155' },
            ],
            xAxisKey: 'name',
            yAxisKeys: ['value'],
            options: { height: 340 },
          },
        },
        // ③ 주목할 패턴
        {
          type: 'insights',
          content: {
            title: '주목할 패턴',
            items: insights,
          },
        },
        // ④ 권장 조치
        {
          type: 'recommendations',
          content: {
            title: '권장 조치',
            items: recommendations,
          },
        },
        // ⑤ 상세 테이블
        {
          type: 'table',
          content: convertToTableFormat(tableData),
        },
      ],
      success: true,
    };
  }

  // 질문 2: 오늘 알람이 가장 많이 발생한 설비는?
  if (message.includes('알람') && message.includes('가장') && (message.includes('많이') || message.includes('발생'))) {
    // 알람 통계 생성
    const alarmStats = [
      { 설비명: 'INJ-003', 알람횟수: 12, 주요알람: '온도 이상', 마지막발생: '14:32' },
      { 설비명: 'INJ-001', 알람횟수: 8, 주요알람: '압력 경고', 마지막발생: '13:15' },
      { 설비명: 'INJ-005', 알람횟수: 5, 주요알람: '사이클 지연', 마지막발생: '11:48' },
      { 설비명: 'INJ-002', 알람횟수: 3, 주요알람: '계량 이상', 마지막발생: '09:22' },
      { 설비명: 'INJ-004', 알람횟수: 1, 주요알람: '통신 오류', 마지막발생: '08:05' }
    ];

    return {
      intent: 'query',
      natural_language: `오늘 알람이 가장 많이 발생한 설비는 **INJ-003**으로 총 12건의 알람이 발생했습니다.\n\n주요 알람 유형은 '온도 이상'이며, 가장 최근 알람은 14:32에 발생했습니다.\n\n해당 설비의 온도 센서 점검을 권장합니다.`,
      blocks: [{
        type: 'table',
        content: convertToTableFormat(alarmStats)
      }],
      success: true
    };
  }

  // 질문 3: 최근 7일간 생산량 추이를 선그래프로 보여줘
  if (message.includes('7일') && message.includes('생산량') && (message.includes('추이') || message.includes('그래프'))) {
    const today = new Date();
    const TARGET = 5000;
    const productionData = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (6 - i));
      const dayOfWeek = date.getDay();
      const dayName = ['일', '월', '화', '수', '목', '금', '토'][dayOfWeek];
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      // 주말은 생산량 감소
      const baseProduction = isWeekend ? 3500 : 5200;
      const production = baseProduction + Math.floor(Math.random() * 800);
      return {
        date: `${date.getMonth() + 1}/${date.getDate()}`,
        dayName,
        isWeekend,
        생산량: production,
        목표: TARGET,
        달성률: Math.round((production / TARGET) * 100),
      };
    });

    const totalProduction = productionData.reduce((s, d) => s + d.생산량, 0);
    const targetTotal = TARGET * 7;
    const avgProduction = Math.round(totalProduction / 7);
    const achievementRate = Math.round((totalProduction / targetTotal) * 100);

    // 평일/주말 분리
    const weekdays = productionData.filter(d => !d.isWeekend);
    const weekends = productionData.filter(d => d.isWeekend);
    const weekdayAvg = weekdays.length ? Math.round(weekdays.reduce((s, d) => s + d.생산량, 0) / weekdays.length) : 0;
    const weekendAvg = weekends.length ? Math.round(weekends.reduce((s, d) => s + d.생산량, 0) / weekends.length) : 0;

    // 최고일/최저일 + 변동 폭
    const sorted = [...productionData].sort((a, b) => b.생산량 - a.생산량);
    const maxDay = sorted[0];
    const minDay = sorted[sorted.length - 1];
    const variance = maxDay.생산량 - minDay.생산량;
    const cv = Math.round((variance / avgProduction) * 100);

    // 추세 (앞 3일 vs 뒤 3일)
    const firstHalf = productionData.slice(0, 3);
    const secondHalf = productionData.slice(4, 7);
    const firstAvg = Math.round(firstHalf.reduce((s, d) => s + d.생산량, 0) / firstHalf.length);
    const secondAvg = Math.round(secondHalf.reduce((s, d) => s + d.생산량, 0) / secondHalf.length);
    const trend = secondAvg - firstAvg;
    const trendPct = Math.round((trend / firstAvg) * 100);

    // 인사이트
    const insights = [];
    if (weekdays.length && weekends.length && Math.abs(weekdayAvg - weekendAvg) > 500) {
      const gapPct = Math.round((1 - weekendAvg / weekdayAvg) * 100);
      insights.push({
        text: `평일 평균 ${weekdayAvg.toLocaleString()}개 vs 주말 평균 ${weekendAvg.toLocaleString()}개 — 주말 생산량이 평일 대비 ${gapPct}% 낮음.`,
        severity: 'info',
      });
    }
    if (achievementRate < 95) {
      insights.push({
        text: `7일 목표 달성률 ${achievementRate}% — 목표 ${(targetTotal - totalProduction).toLocaleString()}개 미달성.`,
        severity: 'warning',
      });
    } else if (achievementRate >= 105) {
      insights.push({
        text: `7일 목표 달성률 ${achievementRate}% — 목표 대비 ${(totalProduction - targetTotal).toLocaleString()}개 초과 달성.`,
        severity: 'info',
      });
    } else {
      insights.push({
        text: `7일 목표 달성률 ${achievementRate}% — 목표 ${TARGET.toLocaleString()}개/일 안정적으로 달성 중.`,
        severity: 'info',
      });
    }
    insights.push({
      text: `최고일 ${maxDay.date}(${maxDay.dayName}) ${maxDay.생산량.toLocaleString()}개, 최저일 ${minDay.date}(${minDay.dayName}) ${minDay.생산량.toLocaleString()}개 — 변동 폭 ${variance.toLocaleString()}개(${cv}%).`,
      severity: cv > 30 ? 'warning' : 'info',
    });
    if (Math.abs(trendPct) >= 10) {
      insights.push({
        text: `최근 3일 평균이 초기 3일 대비 ${trend > 0 ? '+' : ''}${trendPct}% — ${trend > 0 ? '상승 추세' : '하락 추세'} 관찰됨.`,
        severity: trend < 0 ? 'warning' : 'info',
      });
    }

    // 권장 조치
    const recommendations = [];
    if (achievementRate < 95) {
      const dailyGap = Math.round((targetTotal - totalProduction) / 7);
      recommendations.push({
        priority: 'immediate',
        text: `일평균 ${dailyGap.toLocaleString()}개 추가 생산 필요 — 가동률 향상 또는 야간 가동 검토.`,
      });
    }
    if (trend < 0 && Math.abs(trendPct) >= 10) {
      recommendations.push({
        priority: 'today',
        text: `최근 3일 ${Math.abs(trendPct)}% 하락 추세 — 원인 점검 (설비 상태·작업자 배치·원자재) 필요.`,
      });
    }
    if (cv > 30) {
      recommendations.push({
        priority: 'week',
        text: `일별 변동 폭 ${cv}% — 생산 안정성 향상 위한 표준 가동 시간 정착 필요.`,
      });
    }
    recommendations.push({
      priority: 'ongoing',
      text: `평일 평균 ${weekdayAvg.toLocaleString()}개 유지 + 주말 운영 패턴 데이터 누적.`,
    });

    // 자연어 답변
    let trendContext;
    if (trendPct >= 10) trendContext = '상승';
    else if (trendPct <= -10) trendContext = '하락';
    else trendContext = '안정';

    const summaryLine = `최근 7일간 총 **${totalProduction.toLocaleString()}개** 생산 — 일평균 **${avgProduction.toLocaleString()}개**, 목표 달성률 **${achievementRate}%** 입니다.`;
    const trendLine = `최근 3일 평균이 초기 대비 **${trend > 0 ? '+' : ''}${trendPct}%** — **${trendContext} 추세**입니다.`;
    const variabilityLine = `최고 ${maxDay.생산량.toLocaleString()}개(${maxDay.date}) vs 최저 ${minDay.생산량.toLocaleString()}개(${minDay.date}) — 변동 폭 **${cv}%** ${cv > 30 ? '로 안정성 점검이 필요합니다.' : '로 안정적인 수준입니다.'}`;
    const directionLine = achievementRate < 95
      ? `목표 미달분 **${(targetTotal - totalProduction).toLocaleString()}개** — **추가 가동 또는 효율 개선이 필요**합니다.`
      : `목표를 안정적으로 달성 중이며, 현재 운영 패턴 유지가 효과적입니다.`;

    const naturalLanguage = [
      '### 생산량 추이 분석',
      summaryLine,
      trendLine,
      variabilityLine,
      directionLine,
    ].join('\n\n');

    // 테이블
    const tableData = productionData.map(d => ({
      날짜: d.date,
      요일: d.dayName,
      생산량: d.생산량,
      목표: d.목표,
      달성률: `${d.달성률}%`,
      구분: d.isWeekend ? '주말' : '평일',
    }));

    return {
      intent: 'query',
      natural_language: naturalLanguage,
      blocks: [
        // ① KPI 4개
        {
          type: 'stat-cards',
          content: {
            cards: [
              { label: '7일 총 생산량', value: totalProduction.toLocaleString(), unit: '개', accent: '#475569' },
              { label: '일평균',         value: avgProduction.toLocaleString(),   unit: '개', accent: '#475569' },
              { label: '목표 달성률',    value: achievementRate,                  unit: '%',  accent: achievementRate < 95 ? '#dc2626' : '#60a5fa' },
              { label: trend > 0 ? '추세 (상승)' : trend < 0 ? '추세 (하락)' : '추세 (안정)',
                value: `${trend > 0 ? '+' : ''}${trendPct}`, unit: '%',
                accent: trend < 0 && Math.abs(trendPct) >= 10 ? '#dc2626' : '#475569' },
            ],
          },
        },
        // ② 선 차트 — 단정한 스타일 (생산량 강조 + 목표선 점선)
        {
          type: 'chart',
          content: {
            type: 'line',
            title: '최근 7일 생산량 추이',
            data: productionData,
            xAxisKey: 'date',
            yAxisKeys: [
              { key: '생산량', stroke: '#60a5fa', strokeWidth: 2 },
              { key: '목표',   stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '4 4' },
            ],
            options: { height: 320 },
          },
        },
        // ③ 패턴
        {
          type: 'insights',
          content: { title: '주목할 패턴', items: insights },
        },
        // ④ 권장 조치
        {
          type: 'recommendations',
          content: { title: '권장 조치', items: recommendations },
        },
        // ⑤ 일별 테이블
        {
          type: 'table',
          content: convertToTableFormat(tableData),
        },
      ],
      success: true,
    };
  }

  // 질문 4: 사출 1호기에 최근 샷 온도 변화에 대해서 분석해줘
  if (message.includes('사출') && message.includes('1호기') && message.includes('온도') && (message.includes('분석') || message.includes('변화'))) {
    const N = 20;
    const SETPOINT = { 노즐온도: 220, 실린더1: 213, 실린더2: 209, 실린더3: 206, 금형온도: 85 };
    const SERIES_KO = { 노즐온도: '노즐온도', 실린더1: '실린더1', 실린더2: '실린더2', 실린더3: '실린더3', 금형온도: '금형온도' };
    const KEYS = Object.keys(SETPOINT);

    const tempData = Array.from({ length: N }, (_, i) => ({
      샷번호: 1580 + i,
      노즐온도: Math.round((218 + Math.random() * 4 - 2) * 10) / 10,
      실린더1: Math.round((212 + Math.random() * 3 - 1.5) * 10) / 10,
      실린더2: Math.round((208 + Math.random() * 3 - 1.5) * 10) / 10,
      실린더3: Math.round((205 + Math.random() * 4 - 2) * 10) / 10,
      금형온도: Math.round((85 + Math.random() * 2 - 1) * 10) / 10,
    }));

    // 각 시리즈 통계 계산
    const stats = {};
    KEYS.forEach(key => {
      const values = tempData.map(d => d[key]);
      const avg = +(values.reduce((s, v) => s + v, 0) / N).toFixed(1);
      const max = Math.max(...values);
      const min = Math.min(...values);
      const range = +(max - min).toFixed(1);
      const setpoint = SETPOINT[key];
      const deviation = +(((avg - setpoint) / setpoint) * 100).toFixed(2);
      const firstHalfAvg = values.slice(0, 10).reduce((s, v) => s + v, 0) / 10;
      const secondHalfAvg = values.slice(10).reduce((s, v) => s + v, 0) / 10;
      const trend = +(secondHalfAvg - firstHalfAvg).toFixed(2);
      stats[key] = { avg, max, min, range, setpoint, deviation, trend };
    });

    // 가장 편차 큰 시리즈 / 가장 변동 큰 시리즈
    const sortedByDev = [...KEYS].sort((a, b) => Math.abs(stats[b].deviation) - Math.abs(stats[a].deviation));
    const mostDeviated = sortedByDev[0];
    const sortedByRange = [...KEYS].sort((a, b) => stats[b].range - stats[a].range);
    const mostVariable = sortedByRange[0];
    const maxAbsDeviation = Math.abs(stats[mostDeviated].deviation);
    const maxRange = stats[mostVariable].range;

    // 추세 발생 시리즈 (변화량 ≥ 0.5°C)
    const tempsTrending = KEYS.filter(k => Math.abs(stats[k].trend) >= 0.5);

    // 인사이트
    const insights = [];

    // 노즐온도 (가장 중요한 변수) 평가
    if (Math.abs(stats.노즐온도.deviation) >= 1) {
      insights.push({
        text: `노즐온도 평균 ${stats.노즐온도.avg}°C — 설정값 ${stats.노즐온도.setpoint}°C 대비 ${stats.노즐온도.deviation > 0 ? '+' : ''}${stats.노즐온도.deviation}% 편차로 점검이 필요합니다.`,
        severity: 'warning',
      });
    } else {
      insights.push({
        text: `노즐온도 평균 ${stats.노즐온도.avg}°C — 설정값 ${stats.노즐온도.setpoint}°C 대비 ${stats.노즐온도.deviation > 0 ? '+' : ''}${stats.노즐온도.deviation}%, 정상 운영 범위 내.`,
        severity: 'info',
      });
    }

    // 가장 변동성 큰 시리즈
    if (maxRange >= 4) {
      insights.push({
        text: `${SERIES_KO[mostVariable]} 변동 폭 ${maxRange}°C — 5개 시리즈 중 최대, 센서/제어 안정성 확인 필요.`,
        severity: 'warning',
      });
    } else {
      insights.push({
        text: `${SERIES_KO[mostVariable]} 변동 폭 ${maxRange}°C — 5개 시리즈 중 최대지만 허용 범위 내.`,
        severity: 'info',
      });
    }

    // 추세 분석
    if (tempsTrending.length > 0) {
      const trendDetails = tempsTrending
        .map(k => `${SERIES_KO[k]} ${stats[k].trend > 0 ? '+' : ''}${stats[k].trend}°C`)
        .join(', ');
      insights.push({
        text: `최근 10샷 평균 변화: ${trendDetails} — ${tempsTrending.length}개 시리즈에서 점진적 변화 관찰.`,
        severity: 'warning',
      });
    }

    // 안정 시리즈
    const stableSeries = KEYS.filter(k => Math.abs(stats[k].deviation) < 0.5 && stats[k].range < 3);
    if (stableSeries.length > 0) {
      insights.push({
        text: `${stableSeries.length}개 시리즈 (${stableSeries.map(k => SERIES_KO[k]).join(', ')})는 편차 ±0.5% + 변동 폭 3°C 미만으로 매우 안정적.`,
        severity: 'info',
      });
    }

    // 권장 조치
    const recommendations = [];
    if (maxAbsDeviation >= 1) {
      recommendations.push({
        priority: 'immediate',
        text: `${SERIES_KO[mostDeviated]} 편차 ${stats[mostDeviated].deviation > 0 ? '+' : ''}${stats[mostDeviated].deviation}% — 가열 시스템(히터·PID 제어) 점검 필요.`,
      });
    }
    if (maxRange >= 4) {
      recommendations.push({
        priority: 'today',
        text: `${SERIES_KO[mostVariable]} 변동 폭 ${maxRange}°C — 센서 캘리브레이션 + 단열재 상태 확인.`,
      });
    }
    if (tempsTrending.length > 0) {
      recommendations.push({
        priority: 'week',
        text: `점진 변화 시리즈 (${tempsTrending.map(k => SERIES_KO[k]).join(', ')}) — 1주일 추가 추적으로 지속성 확인.`,
      });
    }
    recommendations.push({
      priority: 'ongoing',
      text: `5개 온도 시리즈 모두 설정값 ±0.5% 이내, 변동 폭 3°C 미만 유지 모니터링.`,
    });

    // 자연어 답변
    let stabilityContext;
    if (maxAbsDeviation < 0.5) stabilityContext = '매우 안정적';
    else if (maxAbsDeviation < 1) stabilityContext = '안정적';
    else stabilityContext = '편차 점검 필요';

    const summaryLine = `사출 1호기 최근 **${N}샷**의 5개 온도 시리즈를 분석한 결과 — 전체적으로 **${stabilityContext}** 수준입니다.`;
    const deviationLine = `노즐온도 평균 **${stats.노즐온도.avg}°C** (설정 ${stats.노즐온도.setpoint}°C 대비 **${stats.노즐온도.deviation > 0 ? '+' : ''}${stats.노즐온도.deviation}%**), 최대 편차는 **${SERIES_KO[mostDeviated]} ${stats[mostDeviated].deviation > 0 ? '+' : ''}${stats[mostDeviated].deviation}%** 입니다.`;
    const variabilityLine = `변동 폭은 ${SERIES_KO[mostVariable]}가 **${maxRange}°C**로 가장 크며${maxRange >= 4 ? ' — 센서 또는 가열 안정성 점검이 필요합니다.' : '도 정상 운영 범위 안에 있습니다.'}`;
    const directionLine = maxAbsDeviation >= 1
      ? `**${SERIES_KO[mostDeviated]}의 ${Math.abs(stats[mostDeviated].deviation)}% 편차**를 우선 점검하고, 가열 시스템 상태를 확인하시기 바랍니다.`
      : `현재 5개 온도 시리즈 모두 정상 작동 범위 내에 있으며, 현재 운영 패턴 유지가 효과적입니다.`;

    const naturalLanguage = [
      '### 사출 1호기 온도 분석',
      summaryLine,
      deviationLine,
      variabilityLine,
      directionLine,
    ].join('\n\n');

    // 상세 테이블
    const tableData = tempData.map(d => ({
      샷번호: d.샷번호,
      노즐온도: `${d.노즐온도}°C`,
      실린더1: `${d.실린더1}°C`,
      실린더2: `${d.실린더2}°C`,
      실린더3: `${d.실린더3}°C`,
      금형온도: `${d.금형온도}°C`,
    }));

    return {
      intent: 'query',
      natural_language: naturalLanguage,
      blocks: [
        // ① KPI 4개 — 핵심 지표
        {
          type: 'stat-cards',
          content: {
            cards: [
              { label: '노즐온도 평균', value: stats.노즐온도.avg, unit: '°C',  accent: Math.abs(stats.노즐온도.deviation) >= 1 ? '#dc2626' : '#60a5fa' },
              { label: '노즐 편차',     value: `${stats.노즐온도.deviation > 0 ? '+' : ''}${stats.노즐온도.deviation}`, unit: '%',
                accent: Math.abs(stats.노즐온도.deviation) >= 1 ? '#dc2626' : '#475569' },
              { label: '최대 변동',     value: maxRange, unit: '°C', accent: maxRange >= 4 ? '#dc2626' : '#475569' },
              { label: '분석 샷 수',    value: N, unit: '샷', accent: '#475569' },
            ],
          },
        },
        // ② 선 차트 — 노즐(블루) + 실린더(슬레이트 그라데이션) + 금형(퍼플)
        {
          type: 'chart',
          content: {
            type: 'line',
            title: '온도 변화 추이 (최근 20샷)',
            data: tempData,
            xAxisKey: '샷번호',
            yAxisKeys: [
              { key: '노즐온도', stroke: '#60a5fa', strokeWidth: 2 },
              { key: '실린더1', stroke: '#94a3b8', strokeWidth: 1.5 },
              { key: '실린더2', stroke: '#64748b', strokeWidth: 1.5 },
              { key: '실린더3', stroke: '#475569', strokeWidth: 1.5 },
              { key: '금형온도', stroke: '#a78bfa', strokeWidth: 1.5 },
            ],
            options: { height: 340 },
          },
        },
        // ③ 패턴
        {
          type: 'insights',
          content: { title: '주목할 패턴', items: insights },
        },
        // ④ 권장 조치
        {
          type: 'recommendations',
          content: { title: '권장 조치', items: recommendations },
        },
        // ⑤ 샷별 상세 테이블
        {
          type: 'table',
          content: convertToTableFormat(tableData),
        },
      ],
      success: true,
    };
  }

  return null;
};

export const sendAgentMessage = async (message, conversationHistory = []) => {
  await delay(1000);

  // 추천 질문에 대한 응답 확인
  const recommendedResponse = getRecommendedQuestionResponse(message);
  if (recommendedResponse) {
    return recommendedResponse;
  }

  // 그 외 모든 질문에 대해서는 관리자 문의 안내
  return {
    intent: 'general',
    natural_language: '현재 데모 페이지로 추천 질문만 가능합니다.\n\n실제 서비스에서는 사용자의 질문으로 AI 분석이 가능합니다.\n\n추가 이용 문의: ceo@seoro.ai.kr',
    blocks: [],
    success: true
  };
};

// 스트리밍 시뮬레이션 (데모용)
export const sendAgentMessageStream = async (message, conversationHistory = [], onToken, onStatus, onComplete) => {
  // 상태 업데이트 시뮬레이션
  const statusMessages = [
    '의도 분석 중...',
    '의도: data_query',
    '데이터 조회 중...',
    '결과 생성 중...'
  ];

  for (const status of statusMessages) {
    onStatus(status);
    await delay(300);
  }

  // 추천 질문에 대한 응답 확인
  const response = getRecommendedQuestionResponse(message) || {
    intent: 'general',
    natural_language: '현재 데모 페이지로 추천 질문만 가능합니다.\n\n실제 서비스에서는 사용자의 질문으로 AI 분석이 가능합니다.\n\n추가 이용 문의: ceo@seoro.ai.kr',
    blocks: [],
    success: true
  };

  // 텍스트 스트리밍 시뮬레이션 (글자 단위로 출력)
  const text = response.natural_language || '';
  let currentText = '';

  for (let i = 0; i < text.length; i++) {
    currentText += text[i];
    onToken(text[i], currentText);
    // 랜덤 딜레이로 자연스러운 타이핑 효과
    await delay(15 + Math.random() * 25);
  }

  // 완료
  await delay(200);
  onComplete(response);
};

export const sendAIMessage = async (message, conversationHistory = []) => {
  const result = await sendAgentMessage(message, conversationHistory);
  return {
    response: result.natural_language,
    sqlQuery: null,
    data: result.blocks,
    status: result.success ? 'success' : 'error'
  };
};

// ============================================
// 기타 API
// ============================================

export const updateTargetProduction = async (targetProduction) => {
  await delay();
  return { success: true };
};

export const fetchNotices = async (limit = 10) => {
  await delay();
  return mockNotices.slice(0, limit);
};

export const fetchAllNotices = async (page = 0, size = 1000) => {
  await delay();
  return mockNotices;
};

export const fetchNoticeDetail = async (id) => {
  await delay();
  return mockNotices.find(n => n.id === parseInt(id)) || mockNotices[0];
};

export const createNotice = async (data) => {
  await delay();
  return { id: Date.now(), ...data };
};

export const updateNotice = async (id, data) => {
  await delay();
  return { id, ...data };
};

export const deleteNotice = async (id) => {
  await delay();
  return { success: true };
};

export const fetchRecentAlarms = async (limit = 20) => {
  await delay();
  const alarms = mockRecentAlarms.slice(0, limit);

  // Header.jsx가 기대하는 형식으로 반환
  const errorCount = alarms.filter(a => a.severity === 'ERROR').length;
  const warningCount = alarms.filter(a => a.severity === 'WARNING').length;
  const infoCount = alarms.filter(a => a.severity === 'INFO').length;

  return {
    totalCount: alarms.length,
    errorCount,
    warningCount,
    infoCount,
    recentAlarms: alarms.map(a => ({
      ...a,
      alarmId: a.id
    }))
  };
};

// ============================================
// ETL / System API
// ============================================

export const fetchETLStatus = async () => {
  await delay();
  return mockETLStatus;
};

export const triggerETLSync = async () => {
  await delay();
  return { success: true, message: '동기화가 시작되었습니다.' };
};

export const fetchETLHistory = async (limit = 20) => {
  await delay();
  return mockETLHistory.slice(0, limit);
};

export const fetchDBConnections = async () => {
  await delay();
  return mockDBConnections;
};

export const fetchETLSyncHistory = async (limit = 20) => {
  await delay();
  return mockETLHistory.slice(0, limit);
};

export const fetchLoginLogs = async (page = 0, size = 1000, eventType = null) => {
  await delay();
  let logs = mockLoginLogs;
  if (eventType) {
    logs = logs.filter(l => l.eventType === eventType);
  }
  return logs;
};

export const fetchFailedLoginLogs = async () => {
  await delay();
  return mockLoginLogs.filter(l => !l.success);
};

// ============================================
// System Settings API (Demo)
// ============================================

const mockSystemSettings = {
  systemName: '사출 통합 관제 시스템',
  companyName: '(주)서로',
  companyCode: 'SEORO',
  schemaName: 'seoro',
  businessNumber: '123-45-67890',
  representativeName: '김대표',
  contactEmail: 'ceo@seoro.ai.kr',
  contactPhone: '032-721-7118',
  address: '인천광역시 연수구 송도미래로11번길27, 스마트스퀘어 A동 1501호',
  logoUrl: '',
  faviconUrl: '',
  refreshInterval: '10초',
  timezone: 'Asia/Seoul',
  maxLoginAttempts: 5,
  lockDurationMinutes: 30,
  notifyWeb: true,
  notifyEmail: false,
  notifySms: false,
  notifyPush: false
};

export const fetchSystemSettings = async () => {
  await delay();
  return { success: true, data: mockSystemSettings };
};

export const saveSystemSettings = async (settings) => {
  await delay();
  return { success: true, message: '설정이 저장되었습니다.' };
};

export const uploadLogo = async (file) => {
  await delay();
  return { success: true, data: { logoUrl: '/images/demo-logo.png' } };
};

export const uploadFavicon = async (file) => {
  await delay();
  return { success: true, data: { faviconUrl: '/images/demo-favicon.png' } };
};

// ============================================
// Realtime Shot Status API (Demo)
// ============================================

export const fetchRealtimeShotStatus = async () => {
  await delay();

  // mockEquipments 기반으로 실시간 샷 데이터 생성
  const shotData = mockEquipments.map((e, idx) => ({
    equipmentId: e.id,
    equipmentName: e.equipmentName,
    craftId: ['기어 하우징', '커넥터 케이스', '버튼 캡', '센서 커버'][idx % 4],
    shotCount: 1500 + Math.floor(Math.random() * 5000),
    updateDateTime: new Date().toISOString(),
    tmInjSpeed1: Math.round((80 + Math.random() * 40) * 10) / 10,
    tmInjPress1: Math.round((120 + Math.random() * 60) * 10) / 10,
    tmInjPosi1: Math.round((50 + Math.random() * 30) * 10) / 10,
    tmHoldSpeed1: Math.round((20 + Math.random() * 20) * 10) / 10,
    tmHoldPress1: Math.round((80 + Math.random() * 40) * 10) / 10,
    tmChargePress1: Math.round((60 + Math.random() * 30) * 10) / 10,
    tmChargePosi1: Math.round((100 + Math.random() * 50) * 10) / 10,
    tmInjEndPosi: Math.round((10 + Math.random() * 10) * 10) / 10,
    tmTemp1Current: Math.round((200 + Math.random() * 30) * 10) / 10,
    tmTemp2Current: Math.round((210 + Math.random() * 25) * 10) / 10,
    tmTemp3Current: Math.round((205 + Math.random() * 28) * 10) / 10,
    tmTemp4Current: Math.round((195 + Math.random() * 35) * 10) / 10,
    tmTemp5Current: Math.round((190 + Math.random() * 20) * 10) / 10,
    previousShot: {
      shotCount: 1500 + Math.floor(Math.random() * 5000) - 1,
      tmInjSpeed1: Math.round((80 + Math.random() * 40) * 10) / 10,
      tmInjPress1: Math.round((120 + Math.random() * 60) * 10) / 10,
      tmTemp1Current: Math.round((200 + Math.random() * 30) * 10) / 10,
    }
  }));

  return { success: true, data: shotData };
};

// ============================================
// Report API (일일/주간/월간 보고서)
// ============================================

export const fetchDailyReport = async () => {
  await delay(500);
  return mockDailyReport;
};

export const fetchWeeklyReport = async () => {
  await delay(500);
  return mockWeeklyReport;
};

export const fetchMonthlyReport = async () => {
  await delay(500);
  return mockMonthlyReport;
};
